import {Button, Card, Checkbox, Col, Divider, Flex, Input, Row, Select, Typography} from "antd";
import React from "react";
import {DeleteOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import {ItemOptions} from "./ItemOptions";

export const SurveyEditor = ({survey, setSurvey}) => {
    const addToForm = () => {
        setSurvey({
            ...survey,
            questions: [
                ...survey.questions || [],
                {
                    order: ((survey.questions?.map((d: any) => d.order).sort((a: any, b: any) => b - a) || [0])[0] || 0) + 1
                }
            ]
        })
    }

    const removeItem = (order: any) => {
        const toRemove = new Set<any>([order]);
        let changed = true;
        while (changed) {
            changed = false;
            (survey.questions || []).forEach((q: any) => {
                if (q && q.dependentQuestion && toRemove.has(q.dependentQuestion) && !toRemove.has(q.order)) {
                    toRemove.add(q.order);
                    changed = true;
                }
            });
        }

        const questions = (survey.questions || []).filter((q: any) => q && !toRemove.has(q.order));

        setSurvey({
            ...survey,
            questions
        })
    }

    const removeFromOptionSelect = (order: any, optionOrder: any, key: any) => {
        let optionItem = survey.questions.find((q: any) => q.order === order);
        optionItem = {
            ...optionItem,
            [key]: optionItem[key].map((q: any) => q.order === optionOrder ? null : q).filter((a: any) => a)
        }

        setSurvey({
            ...survey,
            questions: survey.questions.map((q: any) => q.order === order ? optionItem : q)
        })
    }

    const addQuestionDependency = (item: any, option: any) => {
        setSurvey({
            ...survey,
            questions: [
                ...survey.questions || [],
                {
                    order: (survey.questions?.map((d: any) => d.order).sort((a: any, b: any) => b - a) || [0])[0] + 1,
                    dependentQuestion: item,
                    dependentResponse: option,
                }
            ]
        })
    }

    const addToOptionSelect = (order: any, key: any) => {
        let optionItem = survey.questions.find((q: any) => q.order === order);
        optionItem = {
            ...optionItem,
            [key]: [
                ...optionItem[key] || [],
                {
                    order: (optionItem[key]?.map((d: any) => d.order).sort((a: any, b: any) => b - a) || [0])[0] + 1
                }
            ]
        }

        setSurvey({
            ...survey,
            questions: survey.questions.map((q: any) => q.order === order ? optionItem : q)
        });
    }

    const updateQuestion = (order: any, key: any, value: any) => {
        let foundItem = survey.questions.find((item: any) => item.order === order);

        foundItem = {
            ...foundItem,
            [key]: value
        };

        setSurvey({
            ...survey,
            questions: survey.questions.map((item: any) => item.order === order ? foundItem : item)
        })
    }

    const updateOptionItem = (order: any, key: any, value: any, optionOrder: any, itemKey: any) => {
        let optionItem = survey.questions.find((q: any) => q.order === order);
        let optionUpdateItem = optionItem[itemKey].find((q: any) => q.order === optionOrder);

        optionUpdateItem = {
            ...optionUpdateItem,
            [key]: value
        }

        optionItem = {
            ...optionItem,
            [itemKey]: optionItem[itemKey].map((opt: any) => opt.order === optionOrder ? optionUpdateItem : opt)
        }

        setSurvey({
            ...survey,
            questions: survey.questions.map((q: any) => q.order === order ? optionItem : q)
        });
    }

    const getChildren = (parentOrder: any) => (survey.questions || []).filter((a: any) => a.dependentQuestion === parentOrder).sort((a: any, b: any) => a.order - b.order);

    const renderQuestionCard = (item: any, isChild: boolean = false) => (
        <>
            <Col xs={isChild ? 23 : 24} offset={isChild ? 1 : 0} className={isChild ? 'mt-2' : ''}>
                <Card
                    className={isChild ? 'mb-1' : ''}
                    title={isChild ? (
                        <Flex vertical gap={'small'}>
                            <Typography.Text>
                                User
                                answered: {survey.questions.find((q: any) => q.order === item.dependentQuestion)?.options?.find((opt: any) => opt.order === item.dependentResponse)?.description}
                            </Typography.Text>
                            <Input
                                className={'mb-2'}
                                variant={'borderless'}
                                value={item.description}
                                onChange={(e) => updateQuestion(item.order, 'description', e.target.value)}
                                placeholder={'Question Description'}
                            />
                        </Flex>
                    ) : (
                        <Input
                            className={'mb-2'}
                            variant={'borderless'}
                            value={item.description}
                            onChange={(e) => updateQuestion(item.order, 'description', e.target.value)}
                            placeholder={'Question Description'}
                        />
                    )}
                    extra={[
                        <Flex key={'extra-add'} gap={'small'} justify={'space-around'} align={'flex-start'}>
                            <Checkbox key={'req'} checked={item.required}
                                      onChange={(e) => updateQuestion(item.order, 'required', e.target.checked)}>Required?</Checkbox>
                            <Select placeholder={'Question Type'}
                                    onChange={(value) => updateQuestion(item.order, 'type', value)}
                                    value={item.type}
                                    key={'type'}
                                    options={[
                                        {value: 'input', label: 'Short answer'},
                                        {value: 'calendar', label: 'Date'},
                                        {value: 'select', label: 'Multiple choice'},
                                        {value: 'checkbox', label: 'Checkboxes'},
                                        {value: 'grid', label: 'Multiple choice grid'},
                                    ]}/>
                            <Button type={'primary'} className={'ml-1'}
                                    key={'delete'}
                                    onClick={() => removeItem(item.order)}><DeleteOutlined/></Button>
                        </Flex>
                    ]}
                >
                    <ItemOptions
                        items={survey.questions}
                        item={item}
                        addQuestionDependency={addQuestionDependency}
                        updateOptionItem={updateOptionItem}
                        removeFromOptionSelect={removeFromOptionSelect}
                        addToOptionSelect={addToOptionSelect}
                        key={item.order}
                    />
                </Card>
            </Col>
            {getChildren(item.order).map((child: any) => (
                <React.Fragment key={`child-${child.order}`}>
                    {renderQuestionCard(child, true)}
                </React.Fragment>
            ))}
        </>
    );

    return (
        <Col xs={24} className={'mb-2'}>
            <Card title={<Input defaultValue={survey.name}
                                onChange={(e) => setSurvey({...survey, name: e.target.value})}/>}>
                <Row>
                    <Col xs={4}>
                        <Typography.Text>Description:</Typography.Text>
                    </Col>
                    <Col xs={20}>
                        <TextArea defaultValue={survey.description}
                                  autoSize={{minRows: 3}}
                                  onChange={(e) => setSurvey({...survey, description: e.target.value})}/>
                    </Col>
                </Row>
                <Divider/>
                <Row gutter={[8, 8]}>
                    <Col xs={24}>
                        <Typography.Title level={5}>Questions</Typography.Title>
                    </Col>
                    <Col xs={24}>
                        {(survey.questions || [])
                            .filter((a: any) => !a.dependentQuestion)
                            .sort((a: any, b: any) => a.order - b.order)
                            .map((item: any, idx1: any) => (
                                <Row className={'mb-2'} key={idx1} gutter={[8, 8]}>
                                    {renderQuestionCard(item, false)}
                                </Row>
                            ))}
                    </Col>
                </Row>
                <Row className={'mt-2'}>
                    <Col xs={24} sm={8}>
                        <Button type={'primary'} block onClick={() => addToForm()}>Add Question</Button>
                    </Col>
                </Row>
            </Card>
        </Col>
    )
}